// ===========================================
// Enquiry Form Page (Public)
// ===========================================
// Contact/enquiry form for prospective parents and visitors
// Matches the layout and design of MeetAndGreetBookingPage

import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  MusicNote as MusicIcon,
} from '@mui/icons-material';
import { useSchoolInfo, useSchoolInstruments } from '../../hooks/useMeetAndGreet';
import { useSubmitEnquiry } from '../../hooks/useEnquiries';
import SchoolHomeButton from '../../components/public/SchoolHomeButton';
import PublicFooter from '../../components/public/PublicFooter';
import PublicPageShell from '../../components/public/PublicPageShell';
import TurnstileWidget from '../../components/public/TurnstileWidget';
import { useTurnstileGate, isSpamCheckFailed } from '../../hooks/useTurnstileGate';

// ===========================================
// VALIDATION SCHEMA
// ===========================================

const TRIAL_TYPE_OPTIONS = [
  'Meet & Greet',
  'Phone Call',
  'Trial Lesson',
  'School Tour',
  'Other',
];

const enquirySchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email address'),
  phone: z.string().max(30).optional().or(z.literal('')),
  childFirstName: z.string().min(1, "Student's first name is required").max(100),
  instrument: z.string().max(100).optional().or(z.literal('')),
  trialType: z.string().max(100).optional().or(z.literal('')),
  subject: z.string().max(200).optional().or(z.literal('')),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000),
  website: z.string().optional(), // honeypot
});

type EnquiryFormData = z.infer<typeof enquirySchema>;

// ===========================================
// COMPONENT
// ===========================================

export default function EnquiryFormPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const navigate = useNavigate();
  const effectiveSlug = schoolSlug || 'music-n-me';

  const [submitted, setSubmitted] = useState(false);

  // Fetch school info and instruments
  const { data: schoolInfo, isLoading: loadingSchool, error: schoolError } = useSchoolInfo(effectiveSlug);
  const { data: instruments } = useSchoolInstruments(effectiveSlug);

  // #412: Cloudflare Turnstile. The server verifies the token; this only collects it.
  const turnstile = useTurnstileGate(schoolInfo?.turnstileSiteKey);
  const [spamCheckFailed, setSpamCheckFailed] = useState(false);

  // Form mutation
  const submitMutation = useSubmitEnquiry();

  // Form setup
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<EnquiryFormData>({
    resolver: zodResolver(enquirySchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      childFirstName: '',
      instrument: '',
      trialType: '',
      subject: '',
      message: '',
      website: '',
    },
  });

  const formValues = watch();

  const onSubmit = async (data: EnquiryFormData) => {
    if (!schoolInfo) return;

    setSpamCheckFailed(false);
    try {
      await submitMutation.mutateAsync({
        schoolSlug: effectiveSlug,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || undefined,
        childFirstName: data.childFirstName,
        instrument: data.instrument || undefined,
        trialType: data.trialType || undefined,
        subject: data.subject || undefined,
        message: data.message,
        website: data.website || undefined,
        turnstileToken: turnstile.tokenForSubmit,
      });
      setSubmitted(true);
    } catch (error) {
      // Other errors are shown by the mutation; a spam-check refusal is explained inline.
      if (isSpamCheckFailed(error)) setSpamCheckFailed(true);
      // A token is single use, and the server consumes it even when it then refuses
      // (a duplicate booking, an existing family): reset so a retry gets a fresh one.
      // A success unmounts the form, which removes the widget, so no reset is needed.
      turnstile.reset();
    }
  };

  // Loading state
  if (loadingSchool) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  // Error state
  if (schoolError || !schoolInfo) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', py: 8 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            School not found. Please check the URL and try again.
          </Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Container>
      </Box>
    );
  }

  // Success state
  if (submitted) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flex: 1, py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" color="primary" gutterBottom>
              Thank You!
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Your enquiry has been received.
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              We've received your message at <strong>{formValues.email}</strong>.
              A member of our team will be in touch with you shortly.
            </Typography>
            <SchoolHomeButton variant="contained" />
          </Paper>
        </Container>
        <PublicFooter schoolName={schoolInfo?.name} schoolSlug={effectiveSlug} />
      </Box>
    );
  }

  return (
    <PublicPageShell
      title="Get in Touch"
      subtitle="Have a question? We'd love to hear from you. Send us a message and we'll get back to you soon."
      schoolName={schoolInfo?.name}
      schoolSlug={effectiveSlug}
    >

      <Container maxWidth="lg" sx={{ pb: 4, position: 'relative', zIndex: 1, flex: 1 }}>
        <Paper sx={{ p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Your Message
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            {/* Honeypot field - hidden from real users, bots will fill it */}
            <Controller
              name="website"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Website"
                  sx={{ display: 'none' }}
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="off"
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    required
                    sx={{ flex: 1 }}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    required
                    sx={{ flex: 1 }}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Box>

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email Address"
                  type="email"
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                  error={!!errors.email}
                  helperText={errors.email?.message || "We'll reply to this address"}
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone Number (Optional)"
                  fullWidth
                  placeholder="04XX XXX XXX"
                  sx={{ mb: 2 }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />

            <Controller
              name="childFirstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Student's First Name *"
                  fullWidth
                  placeholder="Student's first name"
                  sx={{ mb: 2 }}
                  error={!!errors.childFirstName}
                  helperText={errors.childFirstName?.message}
                />
              )}
            />

            <Controller
              name="instrument"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Instrument of Interest (Optional)</InputLabel>
                  <Select
                    {...field}
                    label="Instrument of Interest (Optional)"
                  >
                    <MenuItem value="">
                      <em>Not sure yet</em>
                    </MenuItem>
                    {instruments?.map((inst: { id: string; name: string }) => (
                      <MenuItem key={inst.id} value={inst.name}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="trialType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Type of Trial (Optional)</InputLabel>
                  <Select
                    {...field}
                    label="Type of Trial (Optional)"
                  >
                    <MenuItem value="">
                      <em>No preference</em>
                    </MenuItem>
                    {TRIAL_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subject (Optional)"
                  fullWidth
                  placeholder="What is your enquiry about?"
                  sx={{ mb: 2 }}
                  error={!!errors.subject}
                  helperText={errors.subject?.message}
                />
              )}
            />

            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Message"
                  fullWidth
                  required
                  multiline
                  rows={5}
                  placeholder="Tell us how we can help you..."
                  sx={{ mb: 3 }}
                  error={!!errors.message}
                  helperText={errors.message?.message}
                />
              )}
            />
          </Box>
        </Paper>

        {/* What to Expect + Submit */}
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
          {/* What to Expect */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              border: 2,
              borderColor: '#96DAC9',
              bgcolor: 'rgba(150, 218, 201, 0.15)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              What Happens Next
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmailIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="We'll receive your message and review it promptly" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PhoneIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="A team member will reply by email or phone within 1-2 business days" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <MusicIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Interested in lessons? Ask us about booking a free Meet & Greet session" />
              </ListItem>
            </List>
          </Paper>

          <TurnstileWidget
            ref={turnstile.widgetRef}
            siteKey={schoolInfo.turnstileSiteKey}
            action="enquiry"
            onToken={turnstile.onToken}
            onUnavailable={turnstile.onUnavailable}
          />
          {spamCheckFailed && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="spam-check-failed">
              The spam check didn&apos;t load or complete, so your message wasn&apos;t sent. Turn off any content
              blocker for this page and try again, or contact {schoolInfo?.name ?? 'the school'} directly.
            </Alert>
          )}

          {isValid && turnstile.waiting && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="turnstile-waiting">
              Checking you&apos;re not a robot…
            </Typography>
          )}

          {/* Submit Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button
              variant="contained"
              size="large"
              disabled={!isValid || !turnstile.ready || submitMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {submitMutation.isPending ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </PublicPageShell>
  );
}
